import React, { Component } from 'react';
import { object, shape, string, func } from 'prop-types';
import { translate } from 'react-i18next';

import { Button } from '@snapask/common-components';

import LineInput from '@dragonfruit/components/fields/LineInput';
import LineTextarea from '@dragonfruit/components/fields/LineTextarea';
import LineSelect from '@dragonfruit/components/fields/LineSelect';
import { STUDENT, TUTOR } from '@dragonfruit/constants/userRoles';
import { Moment } from '@dragonfruit/utils/moment';

import styles from './EditAccountForm.scss';

const cx = require('classnames/bind').bind(styles);

const range = (start, end) =>
  Array(end - start + 1)
    .fill()
    .map((_, i) => start + i);
const years = range(1950, parseInt(new Moment().getFullYear(), 10));
const yearsOptions = years.map(year => ({
  value: year,
  label: year,
}));
const months = range(1, 12);
const monthsOptions = months.map(month => ({
  value: month,
  label: month,
}));
const days = range(1, 31);
const daysOptions = days.map(day => ({
  value: day,
  label: day,
}));

class EditAccountForm extends Component {
  static propTypes = {
    initialValues: shape({
      firstName: string,
      lastName: string,
      username: string,
      gender: object,
      yearOfBirth: object,
      monthOfBirth: object,
      dayOfBirth: object,
      tutorDescription: string,
    }).isRequired,
    t: func.isRequired,
    userRole: string.isRequired,
    onSubmit: func.isRequired,
    onToggleEditAccount: func.isRequired,
    schoolName: string,
  };

  static defaultProps = {
    schoolName: '',
  };

  constructor(props) {
    super(props);

    const {
      firstName,
      lastName,
      username,
      gender,
      yearOfBirth,
      monthOfBirth,
      dayOfBirth,
      tutorDescription,
    } = props.initialValues;
    this.state = {
      firstName: firstName || '',
      lastName: lastName || '',
      username: username || '',
      gender: gender || '',
      yearOfBirth: yearOfBirth || '',
      monthOfBirth: monthOfBirth || '',
      dayOfBirth: dayOfBirth || '',
      tutorDescription: tutorDescription || '',
    };
  }

  handleSubmit = () => {
    this.props.onSubmit(this.state);
  };

  handleUpdateTextInfo = field => e =>
    this.setState({ [field]: e.target.value });

  handleUpdateSelectionInfo = field => e => this.setState({ [field]: e });

  handleEmptyEvent = () => {};

  clickToggleEditAccount = event => this.props.onToggleEditAccount(event);

  render() {
    const { t, userRole, schoolName } = this.props;

    const {
      firstName,
      lastName,
      username,
      gender,
      yearOfBirth,
      monthOfBirth,
      dayOfBirth,
      tutorDescription,
    } = this.state;

    return (
      <div className={styles.root}>
        <div className={styles.doubleInput}>
          <div className={styles.fieldContainer}>
            <p className={styles.field}>{t('account.firstName')}</p>
            <LineInput
              onChange={this.handleEmptyEvent}
              onBlur={this.handleUpdateTextInfo('firstName')}
              value={firstName}
            />
          </div>
          <div className={styles.fieldContainer}>
            <p className={styles.field}>{t('account.lastName')}</p>
            <LineInput
              onChange={this.handleEmptyEvent}
              onBlur={this.handleUpdateTextInfo('lastName')}
              value={lastName}
            />
          </div>
        </div>
        {userRole === STUDENT ? (
          <div className={styles.input}>
            <div className={styles.fieldContainer}>
              <p className={styles.field}>{t('account.username')}</p>
              <LineInput
                onChange={this.handleEmptyEvent}
                onBlur={this.handleUpdateTextInfo('username')}
                value={username}
              />
            </div>
          </div>
        ) : (
          <div className={styles.doubleInput}>
            <div className={styles.fieldContainer}>
              <p className={styles.field}>{t('account.username')}</p>
              <LineInput
                onChange={this.handleEmptyEvent}
                onBlur={this.handleUpdateTextInfo('username')}
                value={username}
              />
            </div>
          </div>
        )}
        {userRole === TUTOR && (
          <React.Fragment>
            <div className={styles.input}>
              <div className={styles.fieldContainer}>
                <p className={styles.field}>{t('account.school')}</p>
                <p className={cx('value', !schoolName && 'disabled')}>
                  {schoolName || t('account.notSelected')}
                </p>
              </div>
            </div>
            <div className={styles.input}>
              <div className={styles.fieldContainer}>
                <p className={styles.field}>{t('account.description.title')}</p>
                <p className={styles.example}>
                  {t('account.description.example')}
                </p>
                <LineTextarea
                  onChange={this.handleEmptyEvent}
                  onBlur={this.handleUpdateTextInfo('tutorDescription')}
                  wordLimit={500}
                  row={2}
                  resize
                  value={tutorDescription}
                />
              </div>
            </div>
          </React.Fragment>
        )}
        {userRole === STUDENT && (
          <div className={styles.input}>
            <div className={styles.fieldContainer}>
              <p className={styles.field}>{t('account.gender')}</p>
              <LineSelect
                clearable={false}
                value={gender.value}
                options={[
                  {
                    value: 'Male',
                    label: t('account.male'),
                  },
                  {
                    value: 'Female',
                    label: t('account.female'),
                  },
                ]}
                onChange={this.handleUpdateSelectionInfo('gender')}
                openOnFocus
                openOnClick
              />
            </div>
          </div>
        )}
        {userRole === STUDENT && (
          <div className={styles.input}>
            <div className={styles.fieldContainer}>
              <p className={styles.field}>{t('account.birthday')}</p>
              <div className={styles.birthValue}>
                <LineSelect
                  clearable={false}
                  value={yearOfBirth.value}
                  options={[
                    ...yearsOptions,
                    {
                      label: t('account.year'),
                      value: '',
                    },
                  ]}
                  onChange={this.handleUpdateSelectionInfo('yearOfBirth')}
                  openOnFocus
                  openOnClick
                />
                <LineSelect
                  clearable={false}
                  value={monthOfBirth.value}
                  options={[
                    ...monthsOptions,
                    {
                      label: t('account.month'),
                      value: '',
                    },
                  ]}
                  onChange={this.handleUpdateSelectionInfo('monthOfBirth')}
                  openOnFocus
                  openOnClick
                />
                <LineSelect
                  clearable={false}
                  value={dayOfBirth.value}
                  options={[
                    ...daysOptions,
                    {
                      label: t('account.day'),
                      value: '',
                    },
                  ]}
                  onChange={this.handleUpdateSelectionInfo('dayOfBirth')}
                  openOnFocus
                  openOnClick
                />
              </div>
            </div>
          </div>
        )}
        <div className={styles.btnContainer}>
          <a onClick={this.clickToggleEditAccount}>{t('account.cancel')}</a>
          <Button
            type="submit"
            text={t('account.save')}
            primary
            onClick={this.handleSubmit}
          />
        </div>
      </div>
    );
  }
}

export default translate(['account'])(EditAccountForm);



// WEBPACK FOOTER //
// ./app/containers/Account/children/AccountProfile/components/EditAccountForm/EditAccountForm.jsx